import * as React from "react";
import {
    Box,
    Stack,
    Tooltip,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    TablePagination,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";

// --------- Datos de ejemplo
const sampleRows = [
    {
        id: 1,
        antimicrobial: "tobramycin",
        class: "aminoglycoside",
        phenotype: "Resistant",
        match: 3,
        genetic: "armA (armA_AY220558)",
    },
];

// ---------- CSV utils
const escapeCSV = (value) => {
    if (value === null || value === undefined) return "";
    const str = String(value);
    const mustQuote = /[",\n\r]/.test(str);
    const escaped = str.replace(/"/g, '""');
    return mustQuote ? `"${escaped}"` : escaped;
};

const normalizeCell = (v) => (Array.isArray(v) ? v.join(", ") : v);

const rowsArrayToCSV = (rows = []) => {
    const keys = rows[0] ? Object.keys(rows[0]) : [];
    const headers = keys.map(escapeCSV);
    const lines = [headers.join(",")];

    for (const r of rows) {
        const line = keys.map((k) => escapeCSV(normalizeCell(r?.[k])));
        lines.push(line.join(","));
    }

    return lines.join("\r\n");
};

function ToolbarCSV({ rows }) {
    const csv = React.useMemo(() => rowsArrayToCSV(rows ?? []), [rows]);

    const copyCSV = async () => {
        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(csv);
                return;
            }
        } catch { }

        const ta = document.createElement("textarea");
        ta.value = csv;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.top = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
    };

    const downloadCSV = () => {
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "wgs_predicted_resistance.csv";
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };

    return (
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
            <Tooltip title="Copiar CSV">
                <IconButton size="small" onClick={copyCSV}>
                    <ContentCopyIcon fontSize="small" />
                </IconButton>
            </Tooltip>
            <Tooltip title="Descargar CSV">
                <IconButton size="small" onClick={downloadCSV}>
                    <DownloadIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        </Stack>
    );
}

export default function PredictedResistanceTable({
    rows = sampleRows,
    emphasizeRow = (r) => r?.phenotype === "Resistant",
    height = 700,
    headerHeight = 60,
    paginationHeight = 52,
}) {
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(10);

    const handleChangePage = (_e, newPage) => setPage(newPage);

    const handleChangeRowsPerPage = (e) => {
        setRowsPerPage(parseInt(e.target.value, 10));
        setPage(0);
    };

    const paginatedRows = rows.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    const bodyHeight = Math.max(height - headerHeight - paginationHeight, 0);
    const rowHeight = rowsPerPage > 0 ? Math.floor(bodyHeight / rowsPerPage) : 0;
    const emptyRowsCount = Math.max(rowsPerPage - paginatedRows.length, 0);

    return (
        <Box sx={{ width: "100%" }}>
            <ToolbarCSV rows={rows} />

            <Paper
                elevation={2}
                sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    height,
                    width: "100%",
                }}
            >
                <TableContainer
                    sx={{
                        flex: 1,
                        width: "100%",
                        overflowX: "auto",
                        overflowY: "auto",
                    }}
                >
                    <Table
                        stickyHeader
                        size="small"
                        aria-label="WGS-predicted resistance"
                        sx={{
                            minWidth: 1100,
                            tableLayout: "fixed",
                            "& th": {
                                bgcolor: "#000",
                                color: "#fff",
                                fontWeight: 700,
                                height: `${headerHeight}px`,
                                whiteSpace: "normal",
                                lineHeight: 1.2,
                            },
                            "& td, & th": {
                                borderColor: "divider",
                            },
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell align="center" sx={{ width: 240, minWidth: 240 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                        Antimicrobial
                                    </Typography>
                                </TableCell>

                                <TableCell align="center" sx={{ width: 180, minWidth: 180 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                        Class
                                    </Typography>
                                </TableCell>

                                <TableCell align="center" sx={{ width: 260, minWidth: 260 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                        WGS-predicted phenotype
                                    </Typography>
                                </TableCell>

                                <TableCell align="center" sx={{ width: 100, minWidth: 100 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                        Match
                                    </Typography>
                                </TableCell>

                                <TableCell align="center" sx={{ width: 320, minWidth: 320 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                        Genetic background
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {paginatedRows.map((r, idx) => {
                                const key = r?.id ?? `${page}-${idx}`;
                                const highlight = emphasizeRow?.(r);

                                return (
                                    <TableRow
                                        key={key}
                                        sx={{
                                            height: rowHeight,
                                        }}
                                    >
                                        <TableCell
                                            align="center"
                                            sx={{
                                                whiteSpace: "normal",
                                                wordBreak: "break-word",
                                            }}
                                        >
                                            <Typography variant="body2">
                                                {r?.antimicrobial ?? "—"}
                                            </Typography>
                                        </TableCell>

                                        <TableCell
                                            align="center"
                                            sx={{
                                                whiteSpace: "normal",
                                                wordBreak: "break-word",
                                            }}
                                        >
                                            <Typography variant="body2">
                                                {r?.class ?? "—"}
                                            </Typography>
                                        </TableCell>

                                        <TableCell
                                            align="center"
                                            sx={{
                                                whiteSpace: "normal",
                                                wordBreak: "break-word",
                                            }}
                                        >
                                            <Typography variant="body2">
                                                {r?.phenotype ?? "—"}
                                            </Typography>
                                        </TableCell>

                                        <TableCell align="center">
                                            <Typography variant="body2">
                                                {r?.match ?? "—"}
                                            </Typography>
                                        </TableCell>

                                        <TableCell
                                            align="center"
                                            sx={{
                                                whiteSpace: "normal",
                                                wordBreak: "break-word",
                                            }}
                                        >
                                            <Typography variant="body2">
                                                {r?.genetic ?? r?.genetic_bg ?? "—"}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}

                            {Array.from({ length: emptyRowsCount }).map((_, i) => (
                                <TableRow key={`empty-${page}-${i}`} sx={{ height: rowHeight }}>
                                    <TableCell colSpan={5} sx={{ borderColor: "divider" }} />
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div"
                    sx={{ mt: "auto", height: `${paginationHeight}px` }}
                    count={rows.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                />
            </Paper>
        </Box>
    );
}